import { useState, useEffect } from "react";
import moment from "moment";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { formatDate } from "helpers/Helpers";
import { ICustomer } from "redux/reducers/models/customer";
import { ICreditCard } from "pages/guest/Customer/CustomerProfile";
import { IAccommodationUnitType } from "pages/secure/facility/accommodation/unitTypes/UnitTypes";
import { IAccommodationCategory } from "pages/secure/facility/accommodation/categories/Categories";
import { INewReservationState } from "pages/secure/facility/accommodation/reservations/types";
import {
  GetAccommodationUnitTypes,
  IGetAccommodationUnitType,
} from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import {
  GetAccommodationCategories,
  IGetAccommodationCategory,
} from "api/rpc/2024-04/facilityAdmin/accommodation/categories";
import {
  GetAccommodationRatePlanPrices,
  IAccommodationRatePlanPrice,
  IGetAccommodationRatePlanPrice,
} from "api/rpc/2024-04/facilityAdmin/accommodation/ratePlans/ratePlanPrices";
import {
  GetCustomer,
  GetCustomerPaymentMethod,
  IGetCustomerPaymentMethodParams,
  IPostPaymentMethodParams,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import {
  IPostAccommodationReservation,
  PostAccommodationCart,
  PostAccommodationReservation,
} from "api/rpc/2024-04/facilityAdmin/accommodation/reservations";
import { IAccommodationReservation as IPortalAccommodationReservation } from "redux/reducers/clientPortal";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js/types/stripe-js/elements";
import { GetAccommodationCategories as GetCustomerAccommodationCategories } from "api/rpc/2024-04/customer/accommodation/categories/categories";
import { GetAccommodationUnitTypes as GetCustomerAccommodationUnitTypes } from "api/rpc/2024-04/customer/accommodation/unitTypes/unitTypes";
import { GetAccommodationRatePlanPrices as GetCustomerAccommodationRatePlanPrices } from "api/rpc/2024-04/customer/accommodation/ratePlans/ratePlanPrices";
import { GetAccommodationCategories as GetGuestAccommodationCategories } from "api/rpc/2024-04/guest/accommodation/categories/categories";
import { GetAccommodationUnitTypes as GetGuestAccommodationUnitTypes } from "api/rpc/2024-04/guest/accommodation/unitTypes/unitTypes";
import { GetAccommodationRatePlanPrices as GetGuestAccommodationRatePlanPrices } from "api/rpc/2024-04/guest/accommodation/ratePlans/ratePlanPrices";
import {
  GetCustomerPaymentMethods as GetCustomerPortalPaymentMethods,
  PostSetup as PostCustomerSetup,
  PostPaymentMethod as PostCustomerPaymentMethod,
  IGetCustomerPaymentMethods,
  IPostCustomerPaymentMethodParams,
} from "api/rpc/2024-04/customer/paymentMethod";
import {
  PostAccommodationReservation as PostCustomerAccommodationReservation,
  IPostPortalAccommodationReservation,
} from "api/rpc/2024-04/customer/accommodation/reservations/reservations";
import { clientPortalUpdate } from "redux/actions/clientPortal";

const initialStepsTemplate = [
  { id: 1, title: "Reservation information", description: "Travel dates and no. of guests" },
  { id: 2, title: "Search availability", description: "Select available unit type" },
  { id: 3, title: "Add guest information", description: "Enter guest details for reservation" },
  { id: 4, title: "Payment confirmation", description: "Enter payment information" },
];

interface IUseAccommodationReservationProps {
  onSuccess?: () => void;
  isAdmin?: boolean;
  facilityId?: number;
}

export function useAccommodationReservation({
  onSuccess: propsOnSuccess,
  isAdmin = true,
  facilityId,
}: IUseAccommodationReservationProps = {}) {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const { clientPortalStore } = useAppSelector(state => state);
  const userLoggedIn = !!clientPortalStore?.user;
  const effectiveFacilityId = isAdmin ? undefined : facilityId ?? clientPortalStore?.selectedFacility?.id;

  // --- Service Abstracting API Calls ---
  const service = {
    getUnitTypes: async (params: IGetAccommodationUnitType, loader: boolean) => {
      return isAdmin
        ? GetAccommodationUnitTypes(params, loader)
        : userLoggedIn
        ? GetCustomerAccommodationUnitTypes({ ...params, facility_id: effectiveFacilityId }, loader)
        : GetGuestAccommodationUnitTypes({ ...params, facility_id: effectiveFacilityId }, loader);
    },
    getCategories: async (params: IGetAccommodationCategory, loader: boolean) => {
      return isAdmin
        ? GetAccommodationCategories(params, loader)
        : userLoggedIn
        ? GetCustomerAccommodationCategories({ facility_id: effectiveFacilityId }, loader)
        : GetGuestAccommodationCategories({ facility_id: effectiveFacilityId }, loader);
    },
    getRatePlanPrices: async (params: IGetAccommodationRatePlanPrice, loader: boolean) => {
      return isAdmin
        ? GetAccommodationRatePlanPrices(params, loader)
        : userLoggedIn
        ? GetCustomerAccommodationRatePlanPrices({ ...params, facility_id: effectiveFacilityId }, loader)
        : GetGuestAccommodationRatePlanPrices({ ...params, facility_id: effectiveFacilityId }, loader);
    },
    getPaymentMethods: async (
      params: IGetCustomerPaymentMethodParams | IGetCustomerPaymentMethods,
      loader: boolean,
    ) => {
      return isAdmin ? GetCustomerPaymentMethod(params, loader) : GetCustomerPortalPaymentMethods(params, loader);
    },
    postReservation: async (
      params: IPostAccommodationReservation | IPostPortalAccommodationReservation,
      loader: boolean,
    ) => {
      return isAdmin
        ? PostAccommodationReservation(params as IPostAccommodationReservation, loader)
        : PostCustomerAccommodationReservation(params as IPostPortalAccommodationReservation, loader);
    },
    postSetup: async (params: { customer_id?: number }, loader: boolean) => {
      return isAdmin
        ? PostSetup(params as { customer_id: number }, loader)
        : PostCustomerSetup({ facility_id: effectiveFacilityId }, loader);
    },
    postPaymentMethod: async (params: IPostPaymentMethodParams | IPostCustomerPaymentMethodParams, loader: boolean) => {
      return isAdmin
        ? PostPaymentMethod(params as IPostPaymentMethodParams, loader)
        : PostCustomerPaymentMethod(params as IPostCustomerPaymentMethodParams, loader);
    },
  };

  // --- State ---
  const [accommodationUnitTypes, setAccommodationUnitTypes] = useState<IAccommodationUnitType[]>([]);
  const [mergedUnitTypes, setMergedUnitTypes] = useState<
    (IAccommodationUnitType & {
      price: number;
      planPriceIds?: number[];
      isSoldOut?: boolean;
      nextAvailableDate?: string;
    })[]
  >([]);
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<IAccommodationUnitType[]>([]);
  const [accommodationCategories, setAccommodationCategories] = useState<IAccommodationCategory[]>([]);
  const [customerPaymentMethods, setCustomerPaymentMethods] = useState<ICreditCard[]>([]);
  const [customerList, setCustomerList] = useState<ICustomer[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [steps, setSteps] = useState(initialStepsTemplate);
  const [rooms, setRooms] = useState(() => {
    if (!isAdmin && clientPortalStore?.accommodationReservation?.rooms) {
      const store = clientPortalStore?.accommodationReservation;
      const totalRooms = store.rooms;
      const totalAdults = store.adults;
      const totalChildren = store.children;
      const adultsPerRoom = Math.floor(totalAdults / totalRooms);
      const childrenPerRoom = Math.floor(totalChildren / totalRooms);
      const extraAdults = totalAdults % totalRooms;
      const extraChildren = totalChildren % totalRooms;

      return Array.from({ length: totalRooms }, (_, index) => ({
        adults: adultsPerRoom + (index < extraAdults ? 1 : 0),
        children: childrenPerRoom + (index < extraChildren ? 1 : 0),
      }));
    }
    return [{ adults: 1, children: 0 }];
  });
  const [reservationCompleteState, setReservationCompleteState] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<ICreditCard>();
  const [newPaymentMethodState, setNewPaymentMethodState] = useState({
    elements: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    newCreditCardCustomerName: "",
  });

  const [billingAddress, setBillingAddress] = useState({
    address: "",
    city: "",
    country: 1,
    postalCode: "",
    province: undefined as number | undefined,
  });

  const [newReservationState, setNewReservationState] = useState<INewReservationState>(() => {
    const portalData = clientPortalStore?.accommodationReservation;
    return {
      openNewReservation: false,
      category_id: !isAdmin ? portalData?.category_id : undefined,
      children: !isAdmin ? portalData?.children : undefined,
      rooms: !isAdmin ? portalData?.rooms : undefined,
      customer: null,
      selectedPaymentMethod: undefined,
      newCreditCardActive: false,
      elements: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
      },
      newCreditCardCustomerName: "",
      subtotalPrice: !isAdmin ? portalData?.subtotalPrice : null,
      duration: !isAdmin ? portalData?.nights : null,
      totalPrice: !isAdmin ? portalData?.totalPrice : null,
      taxAmount: !isAdmin ? portalData?.taxAmount : null,
      confirmationNumber: null,
      customer_id: undefined,
      unit_type_id: !isAdmin ? portalData?.unit_type_id : undefined,
      arrival_date: !isAdmin ? (portalData?.arrival_date ? new Date(portalData.arrival_date) : new Date()) : new Date(),
      departure_date: !isAdmin
        ? portalData?.departure_date
          ? new Date(portalData.departure_date)
          : new Date(new Date().setDate(new Date().getDate() + 1))
        : new Date(new Date().setDate(new Date().getDate() + 1)),
      currency: !isAdmin ? portalData?.currency ?? "" : "",
      adults: !isAdmin ? portalData?.adults : undefined,
      nightly_rates: !isAdmin ? portalData?.nightly_rates ?? [] : [],
    };
  });

  const [billingState, setBillingState] = useState({
    address: "",
    city: "",
    country: 1,
    postalCode: "",
    province: undefined as number | undefined,
  });

  // --- Initialization ---
  useEffect(() => {
    void loadInitialData();
  }, [effectiveFacilityId]);

  useEffect(() => {
    if (!isAdmin) {
      void loadAccommodationUnitTypes();
    }
  }, [isAdmin, newReservationState.arrival_date, newReservationState.departure_date, effectiveFacilityId]);

  useEffect(() => {
    if (!isAdmin && accommodationUnitTypes.length > 0) {
      void loadAccommodationRatePlanPrices(accommodationUnitTypes);
    }
  }, [
    isAdmin,
    accommodationUnitTypes,
    newReservationState.arrival_date,
    newReservationState.departure_date,
    effectiveFacilityId,
  ]);

  function loadInitialData() {
    if (isAdmin) {
      void loadAccommodationUnitTypes();
    }
    void loadAccommodationCategories();
    if (isAdmin) {
      void loadCustomers();
    } else if (userLoggedIn) {
      void loadCustomerPaymentMethods();
    }
  }

  // --- Data Loading ---
  async function loadAccommodationUnitTypes() {
    const params: IGetAccommodationUnitType = {
      start_date: formatDate(newReservationState.arrival_date),
      end_date: formatDate(newReservationState.departure_date),
      with_availability: true,
    };
    const res = await service.getUnitTypes(params, false);
    if (res.status === StatusCode.OK) {
      const data = res.data as IAccommodationUnitType[];
      setAccommodationUnitTypes(data);
      return data;
    }
    return [];
  }

  async function loadCustomers() {
    if (!isAdmin) {
      return;
    }
    const res = await GetCustomer({ "address-lines": true }, true);
    if (res.status === StatusCode.OK) {
      setCustomerList(res?.data);
    }
  }

  async function loadAccommodationCategories() {
    const res = await service.getCategories(null, false);
    if (res.status === StatusCode.OK) {
      setAccommodationCategories(res.data as IAccommodationCategory[]);
    }
  }

  async function loadCustomerPaymentMethods() {
    const params = isAdmin ? { customer_id: newReservationState?.customer?.id } : {};
    if (isAdmin && !params.customer_id) {
      return;
    }

    const res = await service.getPaymentMethods(params, true);
    if (res.status === StatusCode.OK) {
      let paymentMethods: ICreditCard[] = [];
      if (Array.isArray(res.data)) {
        paymentMethods = res.data;
      } else if (res.data && Array.isArray(res.data.card)) {
        paymentMethods = res.data.card;
      } else if (res.data && Array.isArray(res.data.data)) {
        // sometimes wrapped in data
        paymentMethods = res.data.data;
      }

      setCustomerPaymentMethods(paymentMethods || []);
    }
  }

  async function loadAccommodationRatePlanPrices(unitTypes: IAccommodationUnitType[]) {
    const startDate = formatDate(newReservationState?.arrival_date);
    const endDate = formatDate(newReservationState?.departure_date);

    const res = await service.getRatePlanPrices({ start_date: startDate, end_date: endDate }, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    const endDateStr = formatDate(newReservationState?.departure_date);
    const normalizeDate = (d: string | Date) => moment(d).utc().format("YYYY-MM-DD");

    const pricesByUnitAndDate: Record<string, IAccommodationRatePlanPrice[]> = {};
    const nights = Math.max(
      Math.ceil(
        (newReservationState.departure_date.getTime() - newReservationState.arrival_date.getTime()) /
          (1000 * 60 * 60 * 24),
      ),
      1,
    );

    res?.data?.forEach((priceObj: IAccommodationRatePlanPrice) => {
      const dateStr = normalizeDate(priceObj.date);
      if (dateStr === endDateStr) {
        return;
      }
      if (!Number.isFinite(Number(priceObj.price))) {
        return;
      }
      if (priceObj.min_los && nights < priceObj.min_los) {
        return;
      }
      if (priceObj.max_los && nights > priceObj.max_los) {
        return;
      }

      const key = `${priceObj.unit_type_id}::${dateStr}`;
      if (!pricesByUnitAndDate[key]) {
        pricesByUnitAndDate[key] = [];
      }
      pricesByUnitAndDate[key].push(priceObj);
    });

    const updatedMergedUnitTypes = unitTypes?.map(unit => {
      const perDateLowestPrices: number[] = [];
      const planPriceIds: number[] = [];
      const keysForUnit = Object.keys(pricesByUnitAndDate)
        .filter(k => k.startsWith(unit.id + "::"))
        .sort();

      keysForUnit.forEach(key => {
        const priceObjs = pricesByUnitAndDate[key];
        if (!priceObjs || priceObjs.length === 0) {
          return;
        }
        const lowestObj = priceObjs.reduce((min, curr) => (Number(curr.price) < Number(min.price) ? curr : min));
        perDateLowestPrices.push(Number(lowestObj.price));
        planPriceIds.push(lowestObj.id);
      });

      const avgPrice =
        perDateLowestPrices.length > 0
          ? parseFloat((perDateLowestPrices.reduce((a, b) => a + b, 0) / perDateLowestPrices.length).toFixed(2))
          : 0;

      // --- Sold Out Logic ---
      const isSoldOut = (unit.available_units_count ?? 0) < 1;

      return { ...unit, price: avgPrice, planPriceIds, isSoldOut };
    });

    const nightlyRates = updatedMergedUnitTypes?.map(unit => ({
      unit_type_id: unit.id,
      planPriceIds: unit.planPriceIds || [],
    }));

    setMergedUnitTypes(updatedMergedUnitTypes);
    setNewReservationState(prev => ({ ...prev, nightly_rates: nightlyRates }));
  }

  async function priceAccommodationCart() {
    const unitTypeId = selectedUnitTypes?.[0]?.id;
    const nightlyRates = newReservationState.nightly_rates.find(rate => rate.unit_type_id === unitTypeId);
    const planPriceIds = nightlyRates?.planPriceIds ?? [];
    if (!newReservationState.customer?.id || planPriceIds.length === 0) {
      dispatch(showError("Unable to price this stay"));
      return;
    }

    const cartRes = await PostAccommodationCart(
      {
        customer_id: newReservationState.customer.id,
        nightly_rates: planPriceIds,
      },
      true,
    );
    const quote = cartRes.data?.cart_token ? cartRes.data : cartRes.data?.data;
    const errorMessage = cartRes.data?.message;
    if (cartRes.status !== StatusCode.OK || !quote?.cart_token) {
      dispatch(showError(typeof errorMessage === "string" && errorMessage ? errorMessage : "Unable to price this stay"));
      return;
    }

    setNewReservationState(prev => ({
      ...prev,
      subtotalPrice: quote.subtotal_price,
      taxAmount: quote.total_tax,
      totalPrice: quote.total_price,
      cart_token: quote.cart_token,
    }));
  }

  // --- Handlers ---
  function updateStepDescription(stepId: number, newDescription: string) {
    setSteps(prev => prev.map(step => (step.id === stepId ? { ...step, description: newDescription } : step)));
  }

  async function processStepCompletion(stepId: number) {
    switch (stepId) {
      case 1: {
        const totalGuests = rooms?.reduce((acc, room) => acc + room.adults + room.children, 0);
        const nights = Math.max(
          Math.ceil(
            (newReservationState.departure_date.getTime() - newReservationState.arrival_date.getTime()) /
              (1000 * 60 * 60 * 24),
          ),
          0,
        );

        const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
        const start = newReservationState.arrival_date;
        const end = newReservationState.departure_date;
        const dateRange =
          start && end
            ? start.getMonth() === end.getMonth()
              ? `${start.toLocaleDateString("en-US", options)}–${end.getDate()}`
              : `${start.toLocaleDateString("en-US", options)}–${end.toLocaleDateString("en-US", options)}`
            : "—";

        const desc = `${rooms.length} Room${rooms.length !== 1 ? "s" : ""} • ${totalGuests} Guest${
          totalGuests !== 1 ? "s" : ""
        } • ${dateRange}`;
        updateStepDescription(1, desc);
        // const filteredByOccupancy = accommodationUnitTypes.filter(
        //   unit => totalGuests <= unit.max_occupancy && totalGuests >= unit.base_occupancy,
        // );
        const ut = await loadAccommodationUnitTypes();
        await loadAccommodationRatePlanPrices(ut);
        setNewReservationState(prev => ({
          ...prev,
          rooms: rooms.length,
          adults: rooms.reduce((a, r) => a + r.adults, 0),
          children: rooms.reduce((a, r) => a + r.children, 0),
          duration: nights,
        }));
        break;
      }
      case 2: {
        if (selectedUnitTypes?.length > 0) {
          const titles = selectedUnitTypes.map(u => u.title).join(", ");
          const nightlyPrice = selectedUnitTypes.reduce((acc, u) => acc + (u.price ?? 0), 0);
          updateStepDescription(2, `${titles} • $${nightlyPrice}/night`);
          setNewReservationState(prev => ({
            ...prev,
            unit_type_id: selectedUnitTypes[0].id,
            category_id: selectedUnitTypes[0].category_id,
            subtotalPrice: null,
            totalPrice: null,
            taxAmount: null,
            cart_token: undefined,
          }));
        }
        break;
      }
      case 3: {
        if (newReservationState.customer) {
          updateStepDescription(3, newReservationState.customer.full_name);
        }
        void loadCustomerPaymentMethods();
        if (isAdmin) {
          void priceAccommodationCart();
        }
        break;
      }
    }
  }

  const handleContinue = async () => {
    await processStepCompletion(currentStep);
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCheckboxChange = (unit: IAccommodationUnitType) => {
    setSelectedUnitTypes(prev =>
      prev.some(u => u.id === unit.id) ? prev.filter(u => u.id !== unit.id) : [...prev, unit],
    );
  };

  function handleCustomerSelection(customer: ICustomer) {
    setNewReservationState(prev => ({ ...prev, customer: customer, customer_id: customer?.id }));
    if (customer?.address_lines?.length > 0) {
      const addr = customer.address_lines[0];
      setBillingState({
        address: addr.address_line_1 || "",
        city: addr.city || "",
        country: addr.country_id || 1,
        postalCode: addr.postal || "",
        province: addr.province_id,
      });
    }
  }

  function handleCardInputChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setNewReservationState(prev => ({ ...prev, elements: { ...prev.elements, [e.elementType]: e.complete } }));
  }

  async function setUpCard(paymentMethodId: string) {
    const params = isAdmin ? { customer_id: newReservationState.customer.id } : {};
    const res = await service.postSetup(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error setting up card"));
      return;
    }
    const { error, setupIntent } = await stripe.confirmCardSetup(String(res.data.setup_intent.client_secret), {
      payment_method: paymentMethodId,
    });
    if (error) {
      dispatch(showError(error.message));
      return;
    }
    const pmParams = {
      customer_id: isAdmin ? newReservationState.customer.id : undefined,
      payment_method_id: setupIntent.payment_method,
      save_card: true,
    };
    const pmRes = await service.postPaymentMethod(pmParams, true);
    return pmRes.status === StatusCode.OK ? (pmRes.data as ICreditCard) : null;
  }

  async function handleBookReservation(
    additionalParams: {
      billing_address_id?: number;
      billing_address?: any;
      terms_accepted?: boolean;
      newsletter_accepted?: boolean;
      note?: string;
    } = {},
    stripeOverride?: any,
    elementsOverride?: any,
    onSuccess?: () => void,
  ) {
    const stripeInstance = stripeOverride || stripe;
    const elementsInstance = elementsOverride || elements;

    let paymentMethod = selectedPaymentMethod || newReservationState.selectedPaymentMethod;

    if (!paymentMethod && (newReservationState.newCreditCardActive || !isAdmin)) {
      const { error, paymentMethod: pm } = await stripeInstance.createPaymentMethod({
        type: "card",
        card: elementsInstance.getElement("cardNumber"),
        billing_details: !isAdmin
          ? {
              name: clientPortalStore?.user?.full_name,
              email: clientPortalStore?.user?.email,
              phone: clientPortalStore?.user?.phone,
            }
          : undefined,
      });

      if (error) {
        dispatch(showError(String(error.message)));
        return;
      }
      paymentMethod = await setUpCard(String(pm.id));
      if (!paymentMethod) {
        return;
      }
    }

    const unitTypeId = !isAdmin ? newReservationState.unit_type_id : selectedUnitTypes?.[0]?.id;
    const nightlyRates = newReservationState.nightly_rates.find(r => r.unit_type_id === unitTypeId);

    const params: any = {
      customer_id: isAdmin ? newReservationState.customer.id : clientPortalStore?.user?.id,
      unit_type_id: unitTypeId,
      arrival_date: formatDate(newReservationState.arrival_date),
      departure_date: formatDate(newReservationState.departure_date),
      adults: newReservationState.adults,
      children: newReservationState.children,
      quoted_total_amount: newReservationState.totalPrice,
      currency: isAdmin ? "cad" : newReservationState.currency || "cad",
      nightly_rates: nightlyRates ? nightlyRates.planPriceIds : [],
      payment_method_id: paymentMethod?.id,
    };

    if (isAdmin) {
      if (!newReservationState.cart_token) {
        dispatch(showError("Unable to price this stay"));
        return;
      }
      params.cart_token = newReservationState.cart_token;
      params.source = "direct";
      params.billing_address = {
        address_line_1: billingState.address,
        city: billingState.city,
        postal: billingState.postalCode,
        country_id: billingState.country,
        province_id: billingState.province,
      };
    } else {
      params.facility_id = effectiveFacilityId;
      params.nights = newReservationState.duration;
      params.terms_accepted = additionalParams.terms_accepted;
      params.newsletter_accepted = additionalParams.newsletter_accepted;
      params.note = additionalParams.note;
      if (clientPortalStore?.accommodationReservation?.cart_token) {
        params.cart_token = clientPortalStore.accommodationReservation.cart_token;
      }
      if (additionalParams.billing_address_id) {
        params.billing_address_id = additionalParams.billing_address_id;
      } else if (additionalParams.billing_address) {
        params.billing_address = additionalParams.billing_address;
      }
    }

    const res = await service.postReservation(
      params as IPostAccommodationReservation | IPostPortalAccommodationReservation,
      true,
    );
    if (res.status === StatusCode.OK) {
      dispatch(showSuccess("Reservation created successfully"));
      setReservationCompleteState(true);

      if (!isAdmin) {
        const accommodationReservation: IPortalAccommodationReservation = {
          ...clientPortalStore.accommodationReservation,
          confirmationNumber: String(res.data?.id || Math.floor(Math.random() * 1000000)),
        } as IPortalAccommodationReservation;
        dispatch(clientPortalUpdate({ accommodationReservation }));
      }

      setNewReservationState(prev => ({ ...prev, openNewReservation: false }));
      if (onSuccess) {
        onSuccess();
      }
      if (propsOnSuccess) {
        propsOnSuccess();
      }
    } else {
      dispatch(showError("Error creating reservation"));
    }
  }

  function handleOpenNewReservation() {
    setNewReservationState(prev => ({ ...prev, openNewReservation: true }));
  }

  function handleCloseModal() {
    if (!isAdmin) {
      const accommodationReservation = {
        ...clientPortalStore.accommodationReservation,
        confirmationNumber: "",
      };
      dispatch(clientPortalUpdate({ accommodationReservation }));
    }

    setNewReservationState({
      openNewReservation: false,
      category_id: undefined,
      children: undefined,
      rooms: undefined,
      customer: null,
      selectedPaymentMethod: undefined,
      newCreditCardActive: false,
      elements: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
      },
      newCreditCardCustomerName: "",
      subtotalPrice: null,
      duration: null,
      totalPrice: null,
      taxAmount: null,
      confirmationNumber: null,
      customer_id: undefined,
      unit_type_id: undefined,
      arrival_date: new Date(),
      departure_date: new Date(new Date().setDate(new Date().getDate() + 1)),
      currency: "",
      adults: undefined,
      nightly_rates: [],
    });
    setBillingState({ address: "", city: "", country: 1, postalCode: "", province: undefined });
    setReservationCompleteState(false);
    setSelectedUnitTypes([]);
    setCurrentStep(1);
    setSteps(initialStepsTemplate);
    setRooms([{ adults: 1, children: 0 }]);
  }

  function handleCloseConfirmation() {
    handleCloseModal();
  }

  return {
    // State
    accommodationUnitTypes,
    mergedUnitTypes,
    selectedUnitTypes,
    accommodationCategories,
    customerPaymentMethods,
    customerList,
    currentStep,
    steps,
    rooms,
    reservationCompleteState,
    newReservationState,
    billingState,
    selectedPaymentMethod,
    newPaymentMethodState,
    billingAddress,

    // Setters
    setNewReservationState,
    setBillingState,
    setRooms,
    setCurrentStep,
    setSelectedPaymentMethod,
    setNewPaymentMethodState,
    setBillingAddress,

    // Handlers
    handleContinue,
    handleBack,
    handleCheckboxChange,
    handleCustomerSelection,
    handleCardInputChange,
    handleBookReservation,
    handleOpenNewReservation,
    handleCloseConfirmation,
    handleCloseModal,
  };
}
